import * as React from "react";
import { useState, type ReactNode } from "react";
import { City, Country, State } from "country-state-city";
import { format, isValid, parseISO } from "date-fns";
import { CalendarIcon, Check, ChevronDown, Eye, EyeOff, Search, SearchX } from "lucide-react";
import {
  useFormContext,
  useWatch,
  type Control,
  type ControllerRenderProps,
  type ControllerFieldState,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/utils/common.utils";

interface Option {
  label: ReactNode;
  value: string;
  testId?: string;
}
type OptionInput = string | Option;

function toOption(option: OptionInput): Option {
  return typeof option === "string" ? { label: option, value: option } : option;
}

interface FieldShellProps {
  label?: ReactNode;
  description?: ReactNode;
  required?: boolean;
  children: ReactNode;
}

function FieldShell({ label, description = null, required = false, children }: FieldShellProps) {
  return (
    <FormItem className="space-y-1">
      <FormLabel>
        {label}
        {required && <span className="text-red-500" aria-hidden="true"> *</span>}
      </FormLabel>
      {children}
      {description && <FormDescription>{description}</FormDescription>}
      <FormMessage className="mt-0.5 " />
    </FormItem>
  );
}

function useResolvedControl<TFieldValues extends FieldValues>(control?: Control<TFieldValues>) {
  const context = useFormContext<TFieldValues>();
  return control ?? context?.control;
}

interface BaseFieldProps<TFieldValues extends FieldValues> {
  name: FieldPath<TFieldValues>;
  label?: ReactNode;
  description?: ReactNode;
  control?: Control<TFieldValues>;
  required?: boolean;
}

export function FormInput<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description = null,
  control,
  required = false,
  ...props
}: BaseFieldProps<TFieldValues> & Omit<React.ComponentPropsWithoutRef<typeof Input>, "name">) {
  const resolvedControl = useResolvedControl(control);
  const [reveal, setReveal] = useState(false);
  const isPassword = props.type === "password";
  const inputType = isPassword && reveal ? "text" : props.type;

  return (
    <FormField
      control={resolvedControl}
      name={name}
      render={({ field, fieldState }) => (
        <FieldShell label={label} description={description} required={required}>
          <FormControl>
            <div className="relative">
              <Input
                {...field}
                value={field.value ?? ""}
                {...props}
                type={inputType}
                className={cn(isPassword && "pr-10", props.className)}
                aria-invalid={fieldState.invalid}
              />
              {isPassword && (
                <button
                  type="button"
                  onClick={() => setReveal((v) => !v)}
                  tabIndex={-1}
                  aria-label={reveal ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                >
                  {reveal ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              )}
            </div>
          </FormControl>
        </FieldShell>
      )}
    />
  );
}

interface CalendarField {
  value?: string | null;
  onChange: (value: string) => void;
  onBlur: () => void;
  name: string;
  ref: React.Ref<HTMLButtonElement>;
}

interface CalendarControlProps {
  field: CalendarField;
  fieldState: ControllerFieldState;
  placeholder?: string;
  disabled?: boolean;
  calendarProps?: Record<string, unknown>;
}

function CalendarControl({ field, fieldState, placeholder, disabled, calendarProps, ...props }: CalendarControlProps) {
  const [open, setOpen] = useState(false);
  const parsedDate = field.value ? parseISO(String(field.value)) : undefined;
  const selectedDate = parsedDate && isValid(parsedDate) ? parsedDate : undefined;

  const selectDate = (date?: Date) => {
    field.onChange(date ? format(date, "yyyy-MM-dd") : "");
    if (date) setOpen(false);
  };

  return (
    // The calendar is often used inside a modal Dialog. A non-modal popover is
    // portaled outside that dialog and inherits its outside-content pointer
    // lock, making the days visible but impossible to click. Modal mode makes
    // Radix register this popover as the active interactive layer.
    <Popover open={open} onOpenChange={setOpen} modal>
      <FormControl>
        <PopoverTrigger asChild>
          <Button
            ref={field.ref}
            type="button"
            name={field.name}
            variant="outline"
            disabled={disabled}
            onBlur={field.onBlur}
            aria-invalid={fieldState.invalid}
            aria-haspopup="dialog"
            aria-expanded={open}
            className={cn(
              "h-10 w-full justify-start rounded-xl border-dash-border bg-white px-3 text-left font-normal text-dash-ink shadow-none",
              !selectedDate && "text-dash-faint",
            )}
            {...props}
          >
            <CalendarIcon data-icon="inline-start" />
            {selectedDate ? format(selectedDate, "PPP") : placeholder}
          </Button>
        </PopoverTrigger>
      </FormControl>
      <PopoverContent className="w-auto p-0" align="start" onOpenAutoFocus={(event) => event.preventDefault()}>
        <Calendar
          {...(calendarProps as object)}
          mode="single"
          selected={selectedDate}
          // Use the day click event directly. In a controlled single picker it
          // gives us the clicked Date before the library's optional deselect
          // behavior can turn the selection back into undefined.
          onDayClick={selectDate}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

interface FormCalendarProps<TFieldValues extends FieldValues> extends BaseFieldProps<TFieldValues> {
  placeholder?: string;
  disabled?: boolean;
  calendarProps?: Partial<React.ComponentProps<typeof Calendar>>;
}

export function FormCalendar<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description = null,
  control,
  required = false,
  placeholder = "Pick a date",
  disabled = false,
  calendarProps = undefined,
  ...props
}: FormCalendarProps<TFieldValues>) {
  const resolvedControl = useResolvedControl(control);

  return (
    <FormField
      control={resolvedControl}
      name={name}
      render={({ field, fieldState }) => (
        <FieldShell label={label} description={description} required={required}>
          <CalendarControl
            field={field}
            fieldState={fieldState}
            placeholder={placeholder}
            disabled={disabled}
            calendarProps={calendarProps}
            {...props}
          />
        </FieldShell>
      )}
    />
  );
}

export function FormTextarea<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description = null,
  control,
  required = false,
  ...props
}: BaseFieldProps<TFieldValues> & Omit<React.ComponentPropsWithoutRef<typeof Textarea>, "name">) {
  const resolvedControl = useResolvedControl(control);

  return (
    <FormField
      control={resolvedControl}
      name={name}
      render={({ field, fieldState }) => (
        <FieldShell label={label} description={description} required={required}>
          <FormControl>
            <Textarea {...field} {...props} aria-invalid={fieldState.invalid} />
          </FormControl>
        </FieldShell>
      )}
    />
  );
}

interface FormSelectProps<TFieldValues extends FieldValues> extends BaseFieldProps<TFieldValues> {
  options: OptionInput[];
  placeholder?: string;
  triggerClassName?: string;
  disabled?: boolean;
  /** Render a type-to-filter combobox instead of the plain Radix select. */
  searchable?: boolean;
  /** Placeholder for the combobox search input (searchable only). */
  searchPlaceholder?: string;
  "data-testid"?: string;
}

interface SearchSelectControlProps {
  field: ControllerRenderProps<FieldValues, string>;
  fieldState: ControllerFieldState;
  options: Option[];
  placeholder?: string;
  searchPlaceholder?: string;
  triggerClassName?: string;
  disabled?: boolean;
  testId?: string;
}

function SearchSelectControl({
  field,
  fieldState,
  options,
  placeholder,
  searchPlaceholder,
  triggerClassName,
  disabled,
  testId,
}: SearchSelectControlProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);
  const selected = options.find((option) => option.value === field.value);
  // Filter in React rather than leaning on cmdk's built-in filter: this combobox
  // lives inside a modal Radix Dialog, whose focus trap can swallow the raw
  // keystrokes cmdk reads off the input DOM node. A controlled value is immune.
  const q = search.trim().toLowerCase();
  const visible = q
    ? options.filter((option) => String(option.label).toLowerCase().includes(q))
    : options;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch("");
      }}
      modal
    >
      <FormControl>
        <PopoverTrigger asChild>
          <Button
            ref={field.ref as React.Ref<HTMLButtonElement>}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-invalid={fieldState.invalid}
            disabled={disabled}
            onBlur={field.onBlur}
            className={cn(
              "w-full justify-between font-normal",
              !selected && "text-muted-foreground",
              triggerClassName,
            )}
            data-testid={testId}
          >
            <span className="truncate">{selected ? selected.label : placeholder}</span>
            <ChevronDown className="size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
      </FormControl>
      <PopoverContent
        // Render inline (not portalled) so this stays inside any parent Dialog's
        // focus trap — otherwise the search input below cannot be typed into.
        portal={false}
        align="start"
        collisionPadding={16}
        onOpenAutoFocus={(event) => {
          // Focus the search input ourselves. Radix's default sends focus to the
          // content root, and inside a modal Dialog the follow-up focus into a
          // cmdk input never lands — hence the plain input below.
          event.preventDefault();
          inputRef.current?.focus();
        }}
        className="w-[var(--radix-popover-trigger-width)] p-0"
      >
        <div className="flex items-center border-b px-3">
          <Search className="mr-2 size-4 shrink-0 opacity-50" />
          <input
            ref={inputRef}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={searchPlaceholder ?? "Search…"}
            className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            data-testid={testId ? `${testId}-search` : undefined}
          />
        </div>
        <div className="max-h-[240px] overflow-y-auto p-1">
          {visible.length === 0 ? (
            <div className="flex flex-col items-center gap-1.5 px-3 py-8 text-center">
              <SearchX className="size-5 text-muted-foreground/60" />
              <p className="text-sm font-medium text-muted-foreground">No matches</p>
            </div>
          ) : (
            visible.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  field.onChange(option.value);
                  setSearch("");
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center rounded-sm px-2 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground",
                  option.value === field.value && "bg-accent",
                )}
                data-testid={option.testId}
              >
                <Check
                  className={cn("mr-2 size-4", option.value === field.value ? "opacity-100" : "opacity-0")}
                />
                <span className="truncate">{option.label}</span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function FormSelect<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description = null,
  options,
  placeholder = undefined,
  control,
  required = false,
  triggerClassName = undefined,
  searchable = false,
  searchPlaceholder = undefined,
  ...props
}: FormSelectProps<TFieldValues>) {
  const resolvedControl = useResolvedControl(control);
  const testId = props["data-testid"];
  const selectProps = { ...props };
  delete selectProps["data-testid"];
  delete (selectProps as { searchable?: boolean }).searchable;

  return (
    <FormField
      control={resolvedControl}
      name={name}
      render={({ field, fieldState }) => (
        <FieldShell label={label} description={description} required={required}>
          {searchable ? (
            <SearchSelectControl
              field={field as unknown as ControllerRenderProps<FieldValues, string>}
              fieldState={fieldState}
              options={options.map(toOption)}
              placeholder={placeholder}
              searchPlaceholder={searchPlaceholder}
              triggerClassName={triggerClassName}
              disabled={(selectProps as { disabled?: boolean }).disabled}
              testId={testId}
            />
          ) : (
          <Select onValueChange={field.onChange} value={field.value ?? ""} {...selectProps}>
            <FormControl>
              <SelectTrigger
                ref={field.ref}
                className={triggerClassName}
                aria-invalid={fieldState.invalid}
                data-testid={testId}
              >
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectGroup>
                {options.map((option) => {
                  const item = toOption(option);
                  return <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>;
                })}
              </SelectGroup>
            </SelectContent>
          </Select>
          )}
        </FieldShell>
      )}
    />
  );
}

const DEFAULT_COUNTRY = "US";

function useClearWhenMissing<TFieldValues extends FieldValues>(
  name: FieldPath<TFieldValues>,
  options: Option[]
) {
  const { setValue, getValues } = useFormContext<TFieldValues>();
  React.useEffect(() => {
    const current = getValues(name);
    if (current && !options.some((option) => option.value === current)) {
      setValue(name, "" as never, { shouldValidate: false });
    }
  }, [options, name, getValues, setValue]);
}

interface FormCountryProps<TFieldValues extends FieldValues>
  extends Omit<FormSelectProps<TFieldValues>, "options"> {}

export function FormCountry<TFieldValues extends FieldValues = FieldValues>(props: FormCountryProps<TFieldValues>) {
  const options = React.useMemo(
    () =>
      Country.getAllCountries()
        .filter((c) => State.getStatesOfCountry(c.isoCode).length > 0)
        .map((c) => ({ label: c.name, value: c.isoCode })),
    []
  );
  return (
    <FormSelect
      placeholder="Select a country"
      searchable
      searchPlaceholder="Search countries…"
      {...props}
      options={options}
    />
  );
}

interface FormStateProps<TFieldValues extends FieldValues>
  extends Omit<FormSelectProps<TFieldValues>, "options"> {
  countryName?: FieldPath<TFieldValues>;
}

export function FormState<TFieldValues extends FieldValues = FieldValues>({
  countryName,
  control,
  ...props
}: FormStateProps<TFieldValues>) {
  const resolvedControl = useResolvedControl(control);
  const watched = useWatch({ control: resolvedControl, name: countryName as FieldPath<TFieldValues> }) as
    | string
    | undefined;
  const countryCode = countryName ? watched : DEFAULT_COUNTRY;

  const options = React.useMemo(
    () =>
      countryCode
        ? State.getStatesOfCountry(countryCode)
            .filter((s) => City.getCitiesOfState(countryCode, s.isoCode).length > 0)
            .map((s) => ({ label: s.name, value: s.isoCode }))
        : [],
    [countryCode]
  );
  useClearWhenMissing(props.name, options);

  return (
    <FormSelect
      placeholder="Select a state"
      searchable
      searchPlaceholder="Search states…"
      {...props}
      control={control}
      options={options}
      disabled={props.disabled || !countryCode}
    />
  );
}

interface FormCityProps<TFieldValues extends FieldValues>
  extends Omit<FormSelectProps<TFieldValues>, "options"> {
  stateName: FieldPath<TFieldValues>;
  countryName?: FieldPath<TFieldValues>;
}

export function FormCity<TFieldValues extends FieldValues = FieldValues>({
  stateName,
  countryName,
  control,
  ...props
}: FormCityProps<TFieldValues>) {
  const resolvedControl = useResolvedControl(control);
  const stateCode = useWatch({ control: resolvedControl, name: stateName }) as string | undefined;
  const watchedCountry = useWatch({
    control: resolvedControl,
    name: countryName as FieldPath<TFieldValues>,
  }) as string | undefined;
  const countryCode = countryName ? watchedCountry : DEFAULT_COUNTRY;

  const options = React.useMemo(
    () =>
      countryCode && stateCode
        ? City.getCitiesOfState(countryCode, stateCode).map((c) => ({ label: c.name, value: c.name }))
        : [],
    [countryCode, stateCode]
  );
  useClearWhenMissing(props.name, options);

  return (
    <FormSelect
      placeholder="Select a city"
      searchable
      searchPlaceholder="Search cities…"
      {...props}
      control={control}
      options={options}
      disabled={props.disabled || !stateCode}
    />
  );
}

export function FormCheckbox<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description = null,
  control,
  className = undefined,
  ...props
}: BaseFieldProps<TFieldValues> & { className?: string } & Omit<React.ComponentPropsWithoutRef<typeof Checkbox>, "name" | "checked" | "onCheckedChange">) {
  const resolvedControl = useResolvedControl(control);

  return (
    <FormField
      control={resolvedControl}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={cn("flex flex-row items-start gap-3", className)}>
          <FormControl>
            <Checkbox
              checked={field.value}
              onCheckedChange={field.onChange}
              aria-invalid={fieldState.invalid}
              {...props}
            />
          </FormControl>
          <div className="flex flex-col gap-1 leading-none">
            <FormLabel>{label}</FormLabel>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </div>
        </FormItem>
      )}
    />
  );
}

export function FormSwitch<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description = null,
  control,
  className = undefined,
  ...props
}: BaseFieldProps<TFieldValues> & { className?: string } & Omit<React.ComponentPropsWithoutRef<typeof Switch>, "name" | "checked" | "onCheckedChange">) {
  const resolvedControl = useResolvedControl(control);

  return (
    <FormField
      control={resolvedControl}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={cn("flex flex-row items-center justify-between gap-4", className)}>
          <div className="flex flex-col gap-1">
            <FormLabel>{label}</FormLabel>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </div>
          <FormControl>
            <Switch
              checked={field.value}
              onCheckedChange={field.onChange}
              aria-invalid={fieldState.invalid}
              {...props}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
}

interface FormRadioGroupProps<TFieldValues extends FieldValues> extends BaseFieldProps<TFieldValues> {
  options: OptionInput[];
}

export function FormRadioGroup<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description = null,
  options,
  control,
  required = false,
  ...props
}: FormRadioGroupProps<TFieldValues>) {
  const resolvedControl = useResolvedControl(control);

  return (
    <FormField
      control={resolvedControl}
      name={name}
      render={({ field, fieldState }) => (
        <FieldShell label={label} description={description} required={required}>
          <FormControl>
            <RadioGroup
              onValueChange={field.onChange}
              value={field.value}
              aria-invalid={fieldState.invalid}
              className="flex flex-col gap-3"
              {...props}
            >
              {options.map((option) => {
                const item = toOption(option);
                const id = `${name}-${item.value}`;
                return (
                  <div key={item.value} className="flex items-center gap-2">
                    <RadioGroupItem value={item.value} id={id} />
                    <FormLabel htmlFor={id} className="font-normal">{item.label}</FormLabel>
                  </div>
                );
              })}
            </RadioGroup>
          </FormControl>
        </FieldShell>
      )}
    />
  );
}

interface FormToggleGroupProps<TFieldValues extends FieldValues> extends BaseFieldProps<TFieldValues> {
  options: OptionInput[];
  className?: string;
  itemClassName?: string;
  renderOption?: (item: Option, active: boolean) => ReactNode;
}

export function FormToggleGroup<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description = null,
  options,
  control,
  required = false,
  className = undefined,
  itemClassName = undefined,
  renderOption = undefined,
  ...props
}: FormToggleGroupProps<TFieldValues>) {
  const resolvedControl = useResolvedControl(control);

  return (
    <FormField
      control={resolvedControl}
      name={name}
      render={({ field, fieldState }) => (
        <FieldShell label={label} description={description} required={required}>
          <FormControl>
            <ToggleGroup
              type="single"
              value={field.value ?? ""}
              onValueChange={(value) => { if (value) field.onChange(value); }}
              className={className}
              aria-invalid={fieldState.invalid}
              {...props}
            >
              {options.map((option) => {
                const item = toOption(option);
                return (
                  <ToggleGroupItem key={item.value} value={item.value} className={itemClassName} data-testid={item.testId}>
                    {renderOption ? renderOption(item, field.value === item.value) : item.label}
                  </ToggleGroupItem>
                );
              })}
            </ToggleGroup>
          </FormControl>
        </FieldShell>
      )}
    />
  );
}

interface FormCustomFieldProps<TFieldValues extends FieldValues> extends BaseFieldProps<TFieldValues> {
  children: (args: { field: ControllerRenderProps<TFieldValues, FieldPath<TFieldValues>>; fieldState: ControllerFieldState }) => ReactNode;
}

export function FormCustomField<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description = null,
  control,
  required = false,
  children,
}: FormCustomFieldProps<TFieldValues>) {
  const resolvedControl = useResolvedControl(control);

  return (
    <FormField
      control={resolvedControl}
      name={name}
      render={({ field, fieldState }) => (
        <FieldShell label={label} description={description} required={required}>
          {children({ field, fieldState })}
        </FieldShell>
      )}
    />
  );
}
